/**
 * Carbon toggle: a 12px label, then a 48x24 switch with its state word ("On"
 * or "Off") beside it, and an optional helper line. A role="switch" button,
 * so Space and Enter flip it. The switch is on in Soft Green with a
 * near-black knob (a control, not a state color) and off in border-strong.
 * Its round track and knob are `.c-toggle__*` in index.css: the Square Rule's
 * sanctioned exception, beside Tag, Avatar and StatusDot (D39).
 */
import { useId, type ReactNode } from 'react'
import clsx from 'clsx'

export interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: ReactNode
  helper?: ReactNode
  disabled?: boolean
  /** Keep the label for screen readers only. */
  hideLabel?: boolean
  /** State words beside the switch. Default "On" / "Off". */
  onText?: string
  offText?: string
  id?: string
  className?: string
}

export function Toggle({
  checked,
  onChange,
  label,
  helper,
  disabled,
  hideLabel,
  onText = 'On',
  offText = 'Off',
  id: idProp,
  className,
}: ToggleProps) {
  const autoId = useId()
  const id = idProp ?? autoId
  const labelId = `${id}-label`
  const helperId = `${id}-helper`
  return (
    <div className={clsx('flex min-w-0 flex-col items-start', className)}>
      <span id={labelId} className={clsx(hideLabel ? 'sr-only' : 'mb-4 text-helper', disabled ? 'text-text-placeholder' : 'text-text-secondary')}>
        {label}
      </span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={helper ? helperId : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="c-toggle inline-flex items-center gap-2 text-left disabled:cursor-not-allowed"
      >
        <span aria-hidden="true" className="c-toggle__track">
          <span className="c-toggle__knob" />
        </span>
        <span aria-hidden="true" className={clsx('text-body', disabled ? 'text-text-placeholder' : 'text-text-primary')}>
          {checked ? onText : offText}
        </span>
      </button>
      {helper ? (
        <p id={helperId} className={clsx('mt-2 text-helper', disabled ? 'text-text-placeholder' : 'text-text-helper')}>
          {helper}
        </p>
      ) : null}
    </div>
  )
}

export default Toggle
